import React from "react"
import { useSelector } from "react-redux"
import StaffingStatusBadge from "../shared/StaffingStatus"

const NurseSummary = ({ day }) => {
	// Extract nurse recommendations data
	const nurseRecommendationsData = day.metrics?.nurse_recommendations || {
		recommendedRNs: 0,
		recommendedLVNs: 0
	}
	const recommendedRNs = nurseRecommendationsData.recommendedRNs || 0
	const recommendedLVNs = nurseRecommendationsData.recommendedLVNs || 0
	const recommendedNursesCount = recommendedRNs + recommendedLVNs

	// `state.futureSchedules.data` defaults to `{}` in the slice, so an
	// unloaded day looks identical to a legitimately-empty one. The card
	// below uses the staffing counts to drive its content — without a
	// loaded check the RN/LVN/Total values would flash 0 → real numbers
	// when the day's data lands.
	const daySchedule = useSelector(state => state.futureSchedules.data[day.date])
	const dayScheduleLoaded = daySchedule !== undefined
	const currentNurseSchedule = daySchedule?.nurses || []
	const currentStaffingCount = currentNurseSchedule.length

	const currentRNs = currentNurseSchedule.filter(nurse => nurse.nurseRole === "RN").length
	const currentLVNs = currentNurseSchedule.filter(nurse => nurse.nurseRole === "LVN").length

	return (
		<div className="bg-white rounded-lg shadow p-5 flex flex-col">
			<div className="flex items-center justify-between mb-5">
				<h3 className="text-lg font-semibold">Nurse Staffing Summary</h3>
				{dayScheduleLoaded && (
					<StaffingStatusBadge
						currentStaffingCount={currentStaffingCount}
						recommendedNursesCount={recommendedNursesCount}
						currentNurseSchedule={currentNurseSchedule}
						size="md"
					/>
				)}
			</div>

			<div className="space-y-4">
				{/* Recommended Staffing */}
				<div className="p-4 rounded-lg border border-gray-200">
					<h4 className="text-md font-medium text-[#352D66] mb-3">
						Recommended Staffing
					</h4>
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600">RN:</span>
							<span className="font-bold text-lg text-[#352D66]">
								{recommendedRNs}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600">LVN:</span>
							<span className="font-bold text-lg text-[#352D66]">
								{recommendedLVNs}
							</span>
						</div>
					</div>
					<div className="mt-2 pt-2 border-t border-gray-200">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-700">Total:</span>
							<span className="font-bold text-lg text-[#352D66]">
								{recommendedNursesCount}{" "}
								{recommendedNursesCount === 1 ? "Nurse" : "Nurses"}
							</span>
						</div>
					</div>
				</div>

				{/* Current Expected Staffing */}
				<div className="p-4 rounded-lg border border-gray-200">
					<h4 className="text-md font-medium text-gray-700 mb-3">
						Currently Expected Staffing
					</h4>
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600">RN:</span>
							<span className="font-bold text-lg text-gray-700">
								{dayScheduleLoaded ? currentRNs : "—"}
							</span>
						</div>
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600">LVN:</span>
							<span className="font-bold text-lg text-gray-700">
								{dayScheduleLoaded ? currentLVNs : "—"}
							</span>
						</div>
					</div>
					<div className="mt-2 pt-2 border-t border-gray-200">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-700">Total:</span>
							<span className="font-bold text-lg text-gray-700">
								{dayScheduleLoaded
									? `${currentStaffingCount} ${currentStaffingCount === 1 ? "Nurse" : "Nurses"}`
									: "—"}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default NurseSummary
